"use client";

import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/utils/cn";
import * as Slider from "@radix-ui/react-slider";
import { useCallback, useEffect, useState } from "react";
import { useController, type Control, type Path } from "react-hook-form";

const croreMax = 20;
const lakhMin = 20;
const unitsInNumbers: { [key: string]: number } = {
	Lakh: 100000,
	Crore: 10000000,
};

const unitOptions = [
	{ value: "Lakh", label: "Lakh" },
	{ value: "Crore", label: "Crore" },
];

interface BudgetValue {
	min: number;
	max: number;
	value: number;
}

type MinMax = { min: number; max: number };
interface MinMaxStep extends MinMax {
	step: number;
}

const valueCalculate = (value: number, unit: string, multiplier?: number) => {
	if (!unit || !value) return null;
	return value * unitsInNumbers[unit] * (multiplier || 1);
};

const calculateSliderValues = (value: number, unit: string): MinMaxStep | null => {
	switch (unit) {
		case "Crore": {
			const v = value / unitsInNumbers.Crore;
			const maxValue = v >= croreMax ? v : v * 1.1;
			return { min: v * 0.5, max: maxValue, step: 0.1 };
		}
		case "Lakh": {
			const v = value / unitsInNumbers.Lakh;
			return { min: v * 0.5, max: v * 1.1, step: 0.5 };
		}
		default:
			return null;
	}
};

type BudgetFieldProps = {
	name: Path<any>;
	control: Control<any>;
	rules?: Record<string, unknown>;
	className?: string;
	label?: string;
	placeholder?: string;
	disabled?: boolean;
	markFieldDirty?: (fieldName: string) => void; // Hack for manual dirty tracking
	resetSignal?: boolean; // Signal from parent to reset the field
};

export function BudgetField({
	name,
	control,
	rules,
	className,
	label,
	placeholder = "Enter Budget",
	disabled,
	markFieldDirty,
	resetSignal,
}: BudgetFieldProps) {
	const [slider, setSlider] = useState<MinMaxStep | null>(null);
	const [unit, setUnit] = useState("Lakh");
	const [inputValue, setInputValue] = useState("");
	const [minMaxThreshold, setMinMaxThreshold] = useState<[number, number]>([0, 0]);
	const [validationError, setValidationError] = useState<string>("");

	const debouncedInputValue = useDebounce(inputValue, 300);

	// Handle reset signal - clear input immediately when reset is triggered
	useEffect(() => {
		if (resetSignal) {
			setInputValue("");
			setSlider(null);
			setMinMaxThreshold([0, 0]);
			setUnit("Lakh");
			setValidationError("");
		}
	}, [resetSignal]);

	const {
		field: { onChange, value },
		fieldState: { error },
	} = useController({
		name,
		control,
		rules: {
			...rules,
			validate: (budgetValue: BudgetValue) => {
				if (!budgetValue || (!budgetValue.min && !budgetValue.max)) {
					return true; // Allow empty budget
				}
				// Check if the average value is within range
				const avgValue = (budgetValue.min + budgetValue.max) / 2;
				const minAllowed = lakhMin * unitsInNumbers.Lakh;
				const maxAllowed = croreMax * unitsInNumbers.Crore;
				if (avgValue < minAllowed || avgValue > maxAllowed) {
					return `Budget should be between ${lakhMin} Lakh to ${croreMax} Crore`;
				}
				return true;
			},
		},
	});

	// Initialize input value and unit from the current budget value
	// Only set input value from field value on mount or when value changes from outside (not user typing)
	useEffect(() => {
		// If the user is actively editing, don't interfere UNLESS resetSignal is true
		const isUserEditing = document.activeElement && document.activeElement.tagName === "INPUT";
		if (isUserEditing && !resetSignal) return;

		// If value is cleared (min: 0, max: 0), clear the input
		// This handles the "Clear All" or chip clear scenario
		if (value && value.min === 0 && value.max === 0 && value.value === 0 && inputValue !== "") {
			setInputValue("");
			setSlider(null);
			setMinMaxThreshold([0, 0]);
			return;
		}

		// Load budget from URL or form value
		if (value && value.min > 0 && value.max > 0) {
			// Calculate the center value if not provided
			const centerValue = value.value || (value.min + value.max) / 2;
			const selectedUnit = centerValue >= unitsInNumbers.Crore ? "Crore" : "Lakh";
			const divisor = unitsInNumbers[selectedUnit];

			// Only update if we don't have input yet or it's showing "0"
			if (!inputValue || inputValue === "0") {
				if (centerValue >= unitsInNumbers.Crore) {
					setInputValue((centerValue / unitsInNumbers.Crore).toFixed(1));
					setUnit("Crore");
				} else {
					setInputValue((centerValue / unitsInNumbers.Lakh).toFixed(0));
					setUnit("Lakh");
				}

				// Set the slider range and minMaxThreshold from the URL values
				const minInUnit = value.min / divisor;
				const maxInUnit = value.max / divisor;

				setMinMaxThreshold([minInUnit, maxInUnit]);
				setSlider({
					min: minInUnit,
					max: maxInUnit,
					step: selectedUnit === "Crore" ? 0.1 : 0.5,
				});
			}
		}
	}, [value, inputValue, resetSignal]);

	// Memoize the update logic to prevent infinite loops
	// IMPORTANT: Do NOT include 'value' in dependencies to avoid infinite loop
	const updateBudget = useCallback(
		(inputValue: string, selectedUnit: string, currentValue: BudgetValue | undefined) => {
			const numValue = parseFloat(inputValue);
			if (inputValue === "" || inputValue === "0") {
				// Check if currentValue already has a valid budget (from URL or previous state)
				// If so, don't clear it just because inputValue is empty
				const hasValidBudget = currentValue && currentValue.min > 0 && currentValue.max > 0;

				// Check if value is already cleared
				const isAlreadyCleared =
					!currentValue ||
					(currentValue.min === 0 && currentValue.max === 0 && (currentValue.value === 0 || !currentValue.value));

				// If there's a valid budget from URL/form, don't clear it - let the initialization useEffect handle it
				if (hasValidBudget) {
					return;
				}

				if (isAlreadyCleared) {
					// Already cleared, don't update again to prevent infinite loop
					return;
				}
				setValidationError("");
				setSlider(null);
				setMinMaxThreshold([0, 0]);
				onChange({ min: 0, max: 0, value: 0 });
				if (markFieldDirty) {
					markFieldDirty(name);
				}
				return;
			}

			if (!isNaN(numValue) && numValue > 0) {
				const calculatedValue = valueCalculate(numValue, selectedUnit);
				if (calculatedValue) {
					if (calculatedValue >= lakhMin * unitsInNumbers.Lakh && calculatedValue <= croreMax * unitsInNumbers.Crore) {
						setValidationError("");

						// Check if we already have a budget value that matches the calculated value
						// If so, preserve the existing min/max instead of recalculating (important for URL params)
						const hasExistingBudget =
							currentValue && currentValue.value === calculatedValue && currentValue.min > 0 && currentValue.max > 0;

						if (hasExistingBudget) {
							// Preserve existing min/max from URL or previous state
							const existingMinInUnit = currentValue.min / unitsInNumbers[selectedUnit];
							const existingMaxInUnit = currentValue.max / unitsInNumbers[selectedUnit];
							setMinMaxThreshold([existingMinInUnit, existingMaxInUnit]);
							setSlider({
								min: existingMinInUnit,
								max: existingMaxInUnit,
								step: selectedUnit === "Crore" ? 0.1 : 0.5,
							});
							return; // Don't call onChange, we already have the correct values
						}

						// Calculate new min/max for fresh input
						const minThreshHold = selectedUnit === "Crore" ? 1 : 20;
						const min = Math.max(0.5 * numValue, minThreshHold);
						const maxValue = selectedUnit === "Crore" && numValue >= croreMax ? numValue : numValue * 1.1;
						const newThreshold: [number, number] = [min, maxValue];
						const newBudget: BudgetValue = {
							min: newThreshold[0] * unitsInNumbers[selectedUnit],
							max: newThreshold[1] * unitsInNumbers[selectedUnit],
							value: calculatedValue,
						};
						setMinMaxThreshold(newThreshold);
						onChange(newBudget);
						if (markFieldDirty) {
							markFieldDirty(name);
						}
						const newSlider = calculateSliderValues(calculatedValue, selectedUnit);
						setSlider(newSlider);
					} else {
						setValidationError(`Budget should be between ${lakhMin} Lakh to ${croreMax} Crore`);
						setSlider(null);
						setMinMaxThreshold([0, 0]);
					}
				}
			}
		},
		[onChange, markFieldDirty, name], // Only stable dependencies, NOT 'value'
	);

	// Handle debounced input changes
	useEffect(() => {
		// Don't run updateBudget when resetSignal is true - the reset useEffect handles clearing
		if (resetSignal) {
			return;
		}

		updateBudget(debouncedInputValue, unit, value);
	}, [debouncedInputValue, unit, updateBudget, value, resetSignal]);

	const handleSliderChange = (newValues: number[]) => {
		if (newValues.length === 2) {
			const numValue = parseFloat(debouncedInputValue);
			if (!isNaN(numValue) && numValue > 0) {
				// Apply constraints similar to MUI logic
				const calcValue = numValue;
				const lowerMinBound = 0.5 * calcValue;
				const lowerMaxBound = 0.75 * calcValue;
				const upperMinBound = unit === "Crore" && calcValue >= croreMax ? calcValue : 1.05 * calcValue;
				const upperMaxBound = unit === "Crore" && calcValue >= croreMax ? calcValue : 1.1 * calcValue;

				const newAdjustedValue: [number, number] = [
					Math.max(lowerMinBound, Math.min(lowerMaxBound, newValues[0])),
					Math.max(upperMinBound, Math.min(upperMaxBound, newValues[1])),
				];

				const newBudget: BudgetValue = {
					min: newAdjustedValue[0] * unitsInNumbers[unit],
					max: newAdjustedValue[1] * unitsInNumbers[unit],
					value: valueCalculate(numValue, unit) || 0,
				};

				setMinMaxThreshold(newAdjustedValue);
				onChange(newBudget);
				if (markFieldDirty) {
					markFieldDirty(name);
				}
			}
		}
	};

	const formatSliderLabel = (val: number) => {
		const v = parseFloat(val.toString());
		const suffix = v > 100 ? "C" : v < 1 ? "L" : unit.charAt(0);
		return (v > 100 ? v / 100 : v < 1 ? v * 100 : v).toFixed(1) + suffix;
	};

	return (
		<div className={cn("space-y-4", className)}>
			{label && <label className="text-sm font-medium text-gray-900">{label}</label>}

			{/* Input and Unit Selection */}
			<div className="flex items-center gap-2">
				<div className="flex-1">
					<input
						type="number"
						placeholder={placeholder}
						value={inputValue}
						max={unit === "Crore" ? 20 : 2000}
						onChange={e => {
							let val = e.target.value;
							// Auto-switch unit based on input and validation, but allow manual override
							const numVal = parseFloat(val);
							if (val && !isNaN(numVal)) {
								if (unit === "Crore") {
									// Clamp the value instead of modifying DOM directly
									if (numVal > 20) val = croreMax.toString();
								} else if (unit === "Lakh") {
									// Clamp the value instead of modifying DOM directly
									if (numVal > 2000) val = (croreMax * 100).toString();
								}
							}
							setInputValue(val);
						}}
						disabled={disabled}
						className={cn(
							"w-full px-4 py-2 border border-gray-300 rounded-lg",
							"focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
							"disabled:bg-gray-50",
							"placeholder-gray-400 text-sm",
							"disabled:cursor-not-allowed disabled:opacity-50 md:text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
						)}
					/>
				</div>

				<div className="flex rounded-full bg-gray-100 p-1">
					{unitOptions.map(option => (
						<button
							key={option.value}
							type="button"
							onClick={() => {
								setUnit(option.value);
							}}
							className={cn(
								"rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 ease-in-out",
								"focus:outline-none focus:ring-2 focus:ring-primary-300",
								unit === option.value ? "bg-primary-600 text-white shadow-sm" : "bg-transparent text-gray-900",
								disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
							)}
						>
							{option.label}
						</button>
					))}
				</div>
			</div>

			{/* Error Message */}
			{(error || validationError) && <p className="text-sm text-red-600">{validationError || error?.message}</p>}

			{/* Slider */}
			{slider && minMaxThreshold[0] > 0 && (
				<div className="space-y-4">
					<Slider.Root
						className="relative flex items-center select-none touch-none w-full h-5"
						value={minMaxThreshold}
						onValueChange={handleSliderChange}
						min={slider.min}
						max={slider.max}
						step={slider.step}
						minStepsBetweenThumbs={0}
						disabled={disabled}
					>
						<Slider.Track className="bg-gray-300 relative grow rounded-full h-1">
							<Slider.Range className="absolute bg-gray-500 rounded-full h-full" />
						</Slider.Track>
						<Slider.Thumb
							aria-label="Minimum Price"
							className="block w-6 h-6 bg-white border-2 border-gray-500 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
						/>
						<Slider.Thumb
							aria-label="Maximum Price"
							className="block w-6 h-6 bg-white border-2 border-gray-500 rounded-full shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
						/>
					</Slider.Root>

					{/* Slider Labels */}
					<div className="flex justify-between">
						<div className="bg-gray-600 text-white px-2 py-1 rounded text-sm font-medium">
							{formatSliderLabel(minMaxThreshold[0])}
						</div>
						<div className="bg-gray-600 text-white px-2 py-1 rounded text-sm font-medium">
							{formatSliderLabel(minMaxThreshold[1])}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
